"use client"

import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { User, Bookmark, CreditCard, Settings, ChevronRight, PlayCircle, LogOut, AlertTriangle } from "lucide-react"
import { signOut, updateProfile, deleteUser } from "firebase/auth"
import { doc, getDoc, updateDoc, deleteDoc } from "firebase/firestore"
import { auth, db } from "@/lib/firebase"
import { useAuth } from "@/hooks/use-auth"
import { toast } from "sonner"
import { Loader2 } from "lucide-react"

export default function ProfilePage() {
  const navigate = useNavigate()
  const { user, loading } = useAuth()
  const [activeTab, setActiveTab] = useState("purchased")
  const [profileData, setProfileData] = useState<any>(null)
  const [isUpdating, setIsUpdating] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [formData, setFormData] = useState({ name: "", phone: "" })

  useEffect(() => {
    if (loading) return // Wait for firebase auth to hydrate session state
    if (!user) {
      navigate("/login") // Keep unauthorized users out
      return
    }
    
    // Fetch real data from the Firestore ledger
    const fetchRealProfile = async () => {
      try {
        const docRef = doc(db, "users", user.uid)
        const docSnap = await getDoc(docRef)
        if (docSnap.exists()) {
          const data = docSnap.data()
          setProfileData(data)
          setFormData({
             name: data.name || user.displayName || "",
             phone: data.phone || ""
          })
        } else {
          setFormData({ name: user.displayName || "", phone: "" })
        }
      } catch (err) {
        console.error("Failed to fetch profile", err)
      }
    }
    fetchRealProfile()
  }, [user, loading, navigate])

  const handleSignOut = async () => {
    await signOut(auth)
    navigate("/")
  }

  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!user) return
    setIsUpdating(true)
    try {
      if (auth.currentUser) {
         await updateProfile(auth.currentUser, { displayName: formData.name })
      }
      await updateDoc(doc(db, "users", user.uid), {
         name: formData.name,
         phone: formData.phone
      })
      
      const newSnap = await getDoc(doc(db, "users", user.uid))
      if (newSnap.exists()) setProfileData(newSnap.data())
      toast.success("Profile records updated securely!")
    } catch (err) {
      toast.error("Failed to update profile records")
      console.error(err)
    } finally {
      setIsUpdating(false)
    }
  }

  const handleDeleteAccount = async () => {
    if (!window.confirm("Are you absolutely sure you want to permanently delete your account? This action cannot be reversed and all your course access will be lost.")) return;
    
    setIsDeleting(true)
    try {
      if (auth.currentUser && user) {
         await deleteDoc(doc(db, "users", user.uid))
         await deleteUser(auth.currentUser)
         toast.success("Account permanently deleted.")
         navigate("/")
      }
    } catch (err: any) {
      if (err.code === "auth/requires-recent-login") {
         toast.error("Security requirement: Please sign out and sign back in to verify your identity before deleting your account.", { duration: 6000 })
      } else {
         toast.error("Failed to delete account. Please contact technical support.")
      }
      console.error(err)
    } finally {
      setIsDeleting(false)
    }
  }

  const PURCHASED_COURSES = [
    { title: "Indian Polity Deep Dive", date: "Purchase Date: Oct 12, 2024", progress: "64%" },
    { title: "TS Movement Module", date: "Purchase Date: Sep 05, 2024", progress: "12%" }
  ]

  // Fallbacks if data is still querying
  const displayName = profileData?.name || user?.displayName || "Student Scholar"
  const email = profileData?.email || user?.email || "Retrieving email..."

  if (loading) {
     return (
       <div className="min-h-screen bg-slate-50 pt-24 pb-32 flex flex-col items-center justify-center">
          <div className="w-12 h-12 border-4 border-slate-200 border-t-[#b48c36] rounded-full animate-spin mb-4"></div>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Verifying Institutional Identity...</p>
       </div>
     )
  }

  return (
    <div className="min-h-screen bg-slate-50 pt-24 pb-32">
      <div className="max-w-[1200px] mx-auto px-6 sm:px-8">
        
        {/* Profile Header */}
        <div className="flex flex-col md:flex-row items-center md:items-end justify-between gap-8 mb-12 border-b border-slate-200 pb-12 mt-8">
           <div className="flex items-center gap-8">
              <div className="w-20 h-20 bg-slate-900 flex items-center justify-center shadow-lg">
                 <User size={32} className="text-[#b48c36]" />
              </div>
              <div>
                 <h1 className="text-4xl lg:text-4xl font-serif font-medium text-slate-950 tracking-tight mb-2">
                   {displayName}
                 </h1>
                 <p className="text-slate-500 font-sans text-sm">{email}</p>
              </div>
           </div>
           
           <button onClick={handleSignOut} className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-slate-400 hover:text-red-500 transition-colors">
              <LogOut size={14} />
              Sign Out
           </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          
          {/* Settings Ledger Sidebar */}
          <div className="lg:col-span-3 flex flex-row lg:flex-col overflow-x-auto gap-1 border-b lg:border-b-0 border-slate-200 pb-6 lg:pb-0">
             <button onClick={() => setActiveTab('purchased')} className={`flex items-center gap-4 px-5 py-4 border text-left min-w-[200px] lg:min-w-0 transition-all ${activeTab === 'purchased' ? 'bg-white shadow-sm border-slate-200 text-[#b48c36]' : 'border-transparent text-slate-500 hover:text-slate-900 hover:bg-white/50'}`}>
                <PlayCircle size={16} />
                <span className="font-serif text-[17px] font-bold">My Courses</span>
             </button>
             
             <button onClick={() => setActiveTab('settings')} className={`flex items-center gap-4 px-5 py-4 border text-left min-w-[200px] lg:min-w-0 transition-all ${activeTab === 'settings' ? 'bg-white shadow-sm border-slate-200 text-[#b48c36]' : 'border-transparent text-slate-500 hover:text-slate-900 hover:bg-white/50'}`}>
                <Settings size={16} />
                <span className="font-serif text-[17px] font-bold">Settings</span>
             </button>
          </div>

          {/* Profile Portal Content */}
          <div className="lg:col-span-9 animate-in fade-in duration-500">
             
             {activeTab === 'purchased' && (
                <div className="space-y-6">
                   <h2 className="text-2xl font-serif font-medium text-slate-900 mb-6">Your Purchased Modules</h2>
                   <div className="flex flex-col gap-4">
                      {PURCHASED_COURSES.map((course, i) => (
                         <div key={i} className="flex flex-col sm:flex-row justify-between items-start sm:items-center p-6 sm:p-8 bg-white border border-slate-200 shadow-[0_2px_10px_-4px_rgba(0,0,0,0.05)] hover:border-[#b48c36]/30 transition-all group">
                            <div>
                               <h3 className="text-lg sm:text-xl font-serif font-medium text-slate-900 mb-2 truncate">{course.title}</h3>
                               <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{course.date}</p>
                            </div>
                            <div className="w-full sm:w-auto mt-6 sm:mt-0 flex items-center justify-between sm:justify-end gap-8">
                               <div className="flex flex-col items-end">
                                  <span className="text-2xl font-serif font-bold text-slate-900">{course.progress}</span>
                                  <span className="text-[9px] font-bold uppercase tracking-widest text-[#b48c36]">Completed</span>
                               </div>
                               <button className="bg-slate-950 text-white p-4 group-hover:bg-[#b48c36] transition-colors">
                                  <PlayCircle size={20} />
                               </button>
                            </div>
                         </div>
                      ))}
                   </div>
                </div>
             )}

             {activeTab === 'settings' && (
                <div className="bg-white border border-slate-200 p-8 space-y-8 shadow-[0_2px_10px_-4px_rgba(0,0,0,0.05)]">
                   <h2 className="text-2xl font-serif font-medium text-slate-900 border-b border-slate-100 pb-6">Edit Profile</h2>
                   <form onSubmit={handleUpdateProfile} className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                         <div className="space-y-2">
                             <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Full Name</label>
                             <input type="text" required value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} className="w-full border border-slate-200 p-3 text-sm focus:outline-none focus:border-[#b48c36] transition-colors bg-slate-50 text-slate-950" />
                          </div>
                          <div className="space-y-2">
                             <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Email Address</label>
                             <input type="email" value={email} disabled className="w-full border border-slate-100 p-3 text-sm bg-slate-50/50 text-slate-400 opacity-80 cursor-not-allowed" title="Email cannot be changed" />
                          </div>
                      </div>
                      
                      <button type="submit" disabled={isUpdating} className="bg-slate-950 text-white px-8 py-4 text-[10px] font-bold uppercase tracking-[0.2em] hover:bg-[#b48c36] transition-colors flex items-center justify-center gap-2 disabled:opacity-50">
                         {isUpdating ? <Loader2 className="w-4 h-4 animate-spin" /> : "Update Profile"}
                      </button>
                      <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-4">Note: Your email address is permanently locked to this identifier.</p>
                   </form>
                </div>
             )}

          </div>
        </div>

      </div>
    </div>
  )
}
